import { useEffect, useId, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Phone } from 'lucide-react';
import { ALFRED_CUSTOMIZE_PROMPT } from '@wix/ai-scheduling-assistant-constants';
import { Modal, useModalFullScreen } from '../../Modal';
import { BackButton, Button, Card, Toast, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useAgentName } from '../../../lib/use-agent-name';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { messageDir } from '../../../lib/message-direction';
import { queryKeys } from '../../../lib/query-client';
import { fetchAlfredSetup, runAlfredSetup } from '../../../lib/connectors';
import {
  ALFRED_SETUP_STEPS,
  type AlfredForwardingMode,
  type AlfredRouting,
  type AlfredSetupState,
  type AlfredSetupStep,
  DEFAULT_FORWARDING_MODE,
  needsSetup,
  readyCopy,
  stepPosition,
  testCallUrl,
  voiceSummary,
} from '../../../lib/alfred-setup';
import { ltrIsolate } from '../../../lib/alfred-settings';
import { AlfredForwardingPicker } from './AlfredForwardingPicker';
import styles from './AlfredSetupModal.module.css';

const MODAL_BI = { page_name: 'settings-connectors', modal_name: 'alfred_setup' } as const;
const COPY = 'connectors.channelsOut.alfred.setup.';

/**
 * Alfred's onboarding, opened from the Outreach row's Connect (`AlfredOutreachRow`). Two counted steps and a
 * landing, as on both phones (`AlfredOnboardingScreen`): "Alfred's number", "Forward your business calls",
 * then "Alfred is ready".
 *
 * Step 1 opens BEFORE anything is bought: it says that Alfred gets a number of its own and that each call
 * it answers uses AI credits, and only "Get Number" runs the setup. That tap is the user's yes, the one
 * chat asks for before it connects Alfred. Step 2 sets nothing: forwarding is dialled on the user's own
 * phone, so it hands over the codes the server read from Alfred's carrier table. The ready step is Alfred's
 * own "ready" moment: how to test the line, and how callers hear Alfred now, with everything about it left to
 * chat rather than to a form — the same split the settings design draws. The call to Alfred's own number is
 * always there; with forwarding chosen it is joined by how to check the business line (`readyCopy`).
 *
 * Desktop keeps the shell's ✕ and grows a back control from step 2; full-screen on a phone, the shell's own
 * "‹ back" would close the flow from a later step, so it is hidden and this one goes back a step instead.
 */
export function AlfredSetupModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fullScreen = useModalFullScreen();
  const { logModalActionBi } = useGenericBi();
  const openSideChat = useOpenSideChat();
  const agent = useAgentName();
  const titleId = useId();
  const [step, setStep] = useState<AlfredSetupStep>('number');
  const [mode, setMode] = useState<AlfredForwardingMode>(DEFAULT_FORWARDING_MODE);
  const [routing, setRouting] = useState<AlfredRouting>('skipped');
  const [running, setRunning] = useState(false);
  const [setupFailed, setSetupFailed] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Read fresh on every open, never from a cached answer: it decides whether the next tap buys a number.
  const setupQuery = useQuery({ queryKey: queryKeys.alfredSetup, queryFn: fetchAlfredSetup, enabled: open, staleTime: 0, retry: false });
  useLoadBi('alfred-setup', { isLoading: setupQuery.isFetching, isError: setupQuery.isError }, { enabled: open });
  // A failed refetch keeps the last answer in `data`; nothing may act on it while the error is up.
  const setup = setupQuery.isFetching || setupQuery.isError ? null : (setupQuery.data ?? null);

  // A step change swaps the footer's buttons, so the one holding focus unmounts; the new step's title takes it.
  const shownStep = useRef(step);
  useEffect(() => {
    if (shownStep.current === step) return;
    shownStep.current = step;
    if (open) document.getElementById(titleId)?.focus({ preventScroll: true });
  }, [open, step, titleId]);

  const act = (action: string) => logModalActionBi({ ...MODAL_BI, action });

  const close = () => {
    setStep('number');
    setMode(DEFAULT_FORWARDING_MODE);
    setRouting('skipped');
    setSetupFailed(false);
    void queryClient.invalidateQueries({ queryKey: queryKeys.connectors });
    onClose();
  };

  const getNumber = async () => {
    if (running) return;
    act('alfred_setup_get_number');
    setRunning(true);
    setSetupFailed(false);
    try {
      const result = await runAlfredSetup();
      queryClient.setQueryData<AlfredSetupState>(queryKeys.alfredSetup, result);
      void queryClient.invalidateQueries({ queryKey: queryKeys.connectors });
      if (needsSetup(result)) {
        setSetupFailed(true);
      }
    } catch {
      setSetupFailed(true);
    } finally {
      setRunning(false);
    }
  };

  const copy = async (value: string, copiedKey: string, action: string) => {
    act(action);
    try {
      await navigator.clipboard.writeText(value);
      setToast(t(copiedKey));
    } catch {
      // A denied clipboard leaves the text on screen to copy by hand; a second error would be louder than it.
    }
  };

  const back = () => {
    act('alfred_setup_back');
    if (step === 'ready') {
      setStep('routing');
    } else if (step === 'routing') {
      setStep('number');
    } else {
      close();
    }
  };

  const customize = () => {
    act('alfred_setup_customize');
    close();
    openSideChat({ kind: 'new', message: ALFRED_CUSTOMIZE_PROMPT });
  };

  const testCall = (phoneNumber: string) => {
    act('alfred_setup_test_call');
    window.location.assign(testCallUrl(phoneNumber));
  };

  const finishRouting = (outcome: AlfredRouting, action: string) => {
    act(action);
    setRouting(outcome);
    setStep('ready');
  };

  const position = stepPosition(step);
  const ready = setup !== null && !needsSetup(setup);
  const landing = readyCopy(routing);

  const footer =
    step === 'number' ? (
      ready ? (
        <Button
          onClick={() => {
            act('alfred_setup_continue');
            setStep('routing');
          }}
        >
          {t(`${COPY}setUpForwarding`)}
        </Button>
      ) : (
        <Button loading={running} disabled={setup === null} onClick={() => void getNumber()}>
          {t(`${COPY}number.getNumber`)}
        </Button>
      )
    ) : step === 'routing' ? (
      <>
        <Button variant="outlined" onClick={() => finishRouting('skipped', 'alfred_setup_later')}>
          {t(`${COPY}later`)}
        </Button>
        <Button onClick={() => finishRouting(mode, 'alfred_setup_continue')}>{t(`${COPY}continue`)}</Button>
      </>
    ) : (
      <>
        {landing.offersForwarding && (
          <Button
            variant="outlined"
            onClick={() => {
              act('alfred_setup_forwarding');
              setStep('routing');
            }}
          >
            {t(`${COPY}setUpForwarding`)}
          </Button>
        )}
        <Button
          onClick={() => {
            act('alfred_setup_done');
            close();
          }}
        >
          {t(`${COPY}done`)}
        </Button>
      </>
    );

  const number = ltrIsolate(setup?.displayPhoneNumber ?? setup?.phoneNumber ?? '');
  const heading: Record<AlfredSetupStep, { title: string; subtitle: string }> = {
    number: ready
      ? { title: t(`${COPY}number.title`), subtitle: t(`${COPY}number.subtitle`) }
      : { title: t(`${COPY}number.getTitle`), subtitle: t(`${COPY}number.getSubtitle`) },
    routing: { title: t(`${COPY}routing.title`), subtitle: t(`${COPY}routing.subtitle`) },
    ready: { title: t(`${COPY}ready.title`), subtitle: t(`${COPY}${landing.subtitleKey}`, { number }) },
  };
  const voice = setup?.voice ?? null;

  return (
    <Modal
      open={open}
      onClose={close}
      labelledBy={titleId}
      size="minimum"
      maxHeightPx={650}
      scrollBody
      hideClose={fullScreen}
      dismissOnScrimClick={!running}
      cardClassName={styles.card}
      footer={footer}
    >
      <div className={styles.body} data-testid="alfred-setup-modal">
        <div className={styles.progressRow}>
          {step !== 'number' || fullScreen ? <BackButton onClick={back} /> : <span className={styles.progressSpacer} />}
          {position && (
            <span className={styles.dashes} aria-hidden>
              {ALFRED_SETUP_STEPS.map((s, index) => (
                <span key={s} className={index < position.current ? `${styles.dash} ${styles.dashDone}` : styles.dash} />
              ))}
            </span>
          )}
          <span className={styles.progressSpacer} />
        </div>

        <div className={styles.heading}>
          {position && (
            <Typography variant="h4" color="secondary">
              {t(`${COPY}stepLabel`, position)}
            </Typography>
          )}
          <Typography variant="h1" component="h2" id={titleId} tabIndex={-1} className={styles.title}>
            {heading[step].title}
          </Typography>
          <Typography variant="p2" color="primary">
            {heading[step].subtitle}
          </Typography>
        </div>

        {step === 'number' ? (
          <Card variant="sunken" className={styles.numberCard} aria-busy={setupQuery.isFetching}>
            <span className={styles.phoneMark} aria-hidden>
              <Phone size={24} />
            </span>
            {setupQuery.isError ? (
              <>
                <Typography variant="p2" color="secondary" className={styles.centered}>
                  {t(`${COPY}error.load`)}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    act('alfred_setup_retry');
                    void setupQuery.refetch();
                  }}
                >
                  {t(`${COPY}retry`)}
                </Button>
              </>
            ) : setup === null ? (
              <span className={`orion-shimmer ${styles.numberSkeleton}`} aria-hidden />
            ) : ready ? (
              <>
                <Typography variant="h1" component="p" className={styles.number} dir="ltr">
                  {setup.displayPhoneNumber ?? setup.phoneNumber}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => void copy(setup.phoneNumber ?? '', `${COPY}number.copied`, 'alfred_setup_copy_number')}
                >
                  {t(`${COPY}number.copy`)}
                </Button>
              </>
            ) : (
              <>
                <Typography variant="p2" color="secondary" className={styles.centered}>
                  {t(`${COPY}number.disclosure`)}
                </Typography>
                {setupFailed && (
                  <Typography variant="p2" color="primary" className={styles.centered} role="alert">
                    {t(`${COPY}error.setup`)}
                  </Typography>
                )}
              </>
            )}
          </Card>
        ) : step === 'routing' ? (
          <AlfredForwardingPicker
            forwarding={setup?.forwarding ?? null}
            mode={mode}
            ariaLabel={t(`${COPY}routing.title`)}
            onModeChange={(next) => {
              act(`alfred_setup_route_${next}`);
              setMode(next);
            }}
            onCopyCode={(code) => void copy(code, `${COPY}routing.codeCopied`, 'alfred_setup_copy_code')}
          />
        ) : (
          <div className={styles.readyCards}>
            {setup?.phoneNumber && (
              <Card variant="sunken" size="sm" className={styles.readyCard}>
                <Typography variant="h4" color="primary">
                  {t(`${COPY}ready.testCall.title`)}
                </Typography>
                {landing.forwardingCheckKey && (
                  <Typography variant="p2" color="secondary">
                    {t(`${COPY}${landing.forwardingCheckKey}`)}
                  </Typography>
                )}
                <Typography variant="p2" color="secondary">
                  {t(`${COPY}ready.testCall.description`, { number })}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Phone size={16} aria-hidden />}
                  onClick={() => testCall(setup.phoneNumber ?? '')}
                >
                  {t(`${COPY}ready.testCall.cta`, { number })}
                </Button>
              </Card>
            )}
            <Card variant="sunken" size="sm" className={styles.readyCard}>
              <Typography variant="h4" color="primary">
                {t(`${COPY}ready.voice.title`)}
              </Typography>
              {voice && (
                <Typography variant="p2" color="primary">
                  {voiceSummary(
                    voice,
                    voice.tone ? t(`${COPY}ready.voice.tone.${voice.tone}`) : null,
                    voice.gender ? t(`${COPY}ready.voice.gender.${voice.gender}`) : null,
                  )}
                </Typography>
              )}
              {voice?.greeting && (
                // The owner's own words, in whichever language they wrote them.
                <Typography variant="p2" color="secondary" dir={messageDir(voice.greeting)}>
                  {t(`${COPY}ready.voice.greeting`, { greeting: voice.greeting })}
                </Typography>
              )}
              <Typography variant="p2" color="secondary">
                {t(`${COPY}ready.voice.customize`, { maestroAgentName: agent.short })}
              </Typography>
              <Button size="small" variant="outlined" onClick={customize}>
                {t(`${COPY}ready.voice.cta`)}
              </Button>
            </Card>
          </div>
        )}
      </div>
      <Toast open={toast !== null} message={toast ?? ''} severity="success" onClose={() => setToast(null)} />
    </Modal>
  );
}
